import {

  Component,

  Input,

  Output,

  EventEmitter,

  OnChanges,

  SimpleChanges,

  signal,

  computed,

  inject,

  ViewEncapsulation,

} from '@angular/core';

import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';

import { CatalogueApiService } from '@angular-workspace/services';



import type { CanvasDataset } from '../../canvas-catalogue.util';

import {

  extractDataDescriptorFromSearchResponse,

  parseDataDescriptorToAttrFilterRows,

  type AttrFilterRow,

} from './canvas-data-descriptor.util';

import {

  buildDatetimeParam,

  type DatetimeMode,

} from '../dataset-load/canvas-dataset-load.utils';



const CATALOGUE_DATABANK_TYPE = 'adex:DataBank';

const DATA_DESCRIPTOR_SEARCH_SORT = 'itemCreatedAt:desc';



@Component({

  selector: 'lib-canvas-attr-filter-modal',

  standalone: true,

  imports: [CommonModule, FormsModule],

  templateUrl: './canvas-attr-filter-modal.component.html',

  styleUrl: './canvas-attr-filter-modal.component.scss',

  encapsulation: ViewEncapsulation.None,

})

export class CanvasAttrFilterModalComponent implements OnChanges {

  @Input() open = false;

  @Input() dataset: CanvasDataset | null = null;



  @Output() apply = new EventEmitter<Record<string, string>>();

  @Output() close = new EventEmitter<void>();



  private readonly catalogueApi = inject(CatalogueApiService);



  readonly loading = signal(false);

  readonly rows = signal<AttrFilterRow[]>([]);

  readonly checkedKeys = signal<Set<string>>(new Set());

  readonly filterValues = signal<Record<string, string>>({});

  readonly error = signal<string | null>(null);



  readonly datetimeModes = signal<Record<string, DatetimeMode>>({});

  readonly datetimeSingle = signal<Record<string, string>>({});

  readonly datetimeFrom = signal<Record<string, string>>({});

  readonly datetimeTo = signal<Record<string, string>>({});



  readonly hasDateTimeRows = computed(() =>

    this.rows().some((r) => r.isDateTime),

  );



  readonly hasSelection = computed(() => {

    const checked = this.checkedKeys();

    const values = this.filterValues();

    const modes = this.datetimeModes();

    const single = this.datetimeSingle();

    const from = this.datetimeFrom();

    const to = this.datetimeTo();



    for (const key of checked) {

      const row = this.rows().find((r) => r.key === key);

      if (row?.isDateTime) {

        const mode = modes[key] ?? 'equal';

        if (mode === 'interval') {

          if ((from[key] ?? '').trim() && (to[key] ?? '').trim()) return true;

        } else if ((single[key] ?? '').trim()) {

          return true;

        }

      } else if ((values[key] ?? '').trim()) {

        return true;

      }

    }

    return false;

  });



  ngOnChanges(changes: SimpleChanges): void {

    const openChange = changes['open'];

    const datasetChange = changes['dataset'];



    if ((openChange || datasetChange) && this.open && this.dataset) {

      this.reset();

      void this.loadDescriptor(this.dataset.id);

    }



    if (openChange && !this.open) {

      this.reset();

    }

  }



  private reset(): void {

    this.rows.set([]);

    this.checkedKeys.set(new Set());

    this.filterValues.set({});

    this.datetimeModes.set({});

    this.datetimeSingle.set({});

    this.datetimeFrom.set({});

    this.datetimeTo.set({});

    this.error.set(null);

    this.loading.set(false);

  }



  private async loadDescriptor(datasetId: string): Promise<void> {

    this.loading.set(true);

    this.error.set(null);

    try {

      const response = await this.catalogueApi.searchAllAssets(

        '',

        [],

        ['dataDescriptor'],

        1,

        100,

        DATA_DESCRIPTOR_SEARCH_SORT,

        [

          {

            searchType: 'term',

            field: 'type',

            values: [CATALOGUE_DATABANK_TYPE],

          },

          {

            searchType: 'term',

            field: 'id',

            values: [datasetId],

          },

        ],

      );



      const descriptor = extractDataDescriptorFromSearchResponse(

        response,

        datasetId,

      );

      const rows = parseDataDescriptorToAttrFilterRows(descriptor);

      this.rows.set(rows);



      const defaultModes: Record<string, DatetimeMode> = {};

      for (const row of rows) {

        if (row.isDateTime) {

          defaultModes[row.key] = 'equal';

        }

      }

      this.datetimeModes.set(defaultModes);



      if (rows.length === 0) {

        this.error.set('No filterable attributes found for this dataset.');

      }

    } catch {

      this.error.set('Failed to load attributes. Please try again.');

    } finally {

      this.loading.set(false);

    }

  }



  toggleKey(key: string): void {

    this.checkedKeys.update((prev) => {

      const next = new Set(prev);

      if (next.has(key)) {

        next.delete(key);

      } else {

        next.add(key);

      }

      return next;

    });

  }



  isChecked(key: string): boolean {

    return this.checkedKeys().has(key);

  }



  getValue(key: string): string {

    return this.filterValues()[key] ?? '';

  }



  setValue(key: string, value: string): void {

    this.filterValues.update((prev) => ({ ...prev, [key]: value }));

  }



  getDatetimeMode(key: string): DatetimeMode {

    return this.datetimeModes()[key] ?? 'equal';

  }



  setDatetimeMode(key: string, mode: DatetimeMode): void {

    this.datetimeModes.update((prev) => ({ ...prev, [key]: mode }));

  }



  getDatetimeSingle(key: string): string {

    return this.datetimeSingle()[key] ?? '';

  }



  setDatetimeSingle(key: string, value: string): void {

    this.datetimeSingle.update((prev) => ({ ...prev, [key]: value }));

  }



  getDatetimeFrom(key: string): string {

    return this.datetimeFrom()[key] ?? '';

  }



  setDatetimeFrom(key: string, value: string): void {

    this.datetimeFrom.update((prev) => ({ ...prev, [key]: value }));

  }



  getDatetimeTo(key: string): string {

    return this.datetimeTo()[key] ?? '';

  }



  setDatetimeTo(key: string, value: string): void {

    this.datetimeTo.update((prev) => ({ ...prev, [key]: value }));

  }



  onApply(): void {

    const checked = this.checkedKeys();

    const values = this.filterValues();

    const filters: Record<string, string> = {};



    for (const key of checked) {

      const row = this.rows().find((r) => r.key === key);

      if (row?.isDateTime) {

        const mode = this.getDatetimeMode(key);

        const param = buildDatetimeParam(

          mode,

          this.getDatetimeSingle(key),

          this.getDatetimeFrom(key),

          this.getDatetimeTo(key),

        );

        if (param) {

          filters['datetime'] = param;

        }

      } else {

        const v = (values[key] ?? '').trim();

        if (v) filters[key] = v;

      }

    }

    this.apply.emit(filters);

  }



  onClose(): void {

    this.close.emit();

  }



  onBackdropClick(event: MouseEvent): void {

    if ((event.target as HTMLElement).classList.contains('cv-af-backdrop')) {

      this.onClose();

    }

  }

}


